"use client";

import { useState } from "react";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/providers/auth-provider";
import { Button } from "@/components/ui/button";
import { ErrorAlert } from "@/components/ui/feedback";
import { InputWithIcon, Label } from "@/components/ui/input";
import { Icon } from "@/components/ui/icon";

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Sign in failed");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-surface">
      <main className="w-full max-w-[480px] px-gutter">
        {/* Brand Header */}
        <div className="mb-section_gap text-center">
          <div className="mb-stack_md flex justify-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-primary shadow-lg">
              <Icon name="sports_soccer" size="xl" className="text-on-primary" />
            </div>
          </div>
          <h1 className="mb-2 text-display-lg font-bold tracking-tight text-on-surface">
            Sporush
          </h1>
          <p className="text-body-lg font-semibold uppercase tracking-widest text-secondary opacity-70">
            Athletic Intelligence
          </p>
        </div>

        {/* Login Card */}
        <div className="rounded-xl border border-outline-variant bg-surface-container-lowest p-10 shadow-card transition-shadow duration-300 hover:shadow-card-hover">
          <div className="mb-section_gap">
            <h2 className="mb-2 text-headline-md font-semibold text-on-surface">Admin Portal</h2>
            <p className="text-body-md text-on-surface-variant">
              Please enter your credentials to access the dashboard.
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-stack_md">
            {error ? <ErrorAlert message={error} /> : null}

            <div className="space-y-stack_sm">
              <Label htmlFor="email">Admin ID</Label>
              <InputWithIcon
                id="email"
                icon="badge"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your unique ID"
              />
            </div>

            <div className="space-y-stack_sm">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <a
                  href="#"
                  className="text-label-md font-semibold text-primary transition-all hover:underline"
                  onClick={(e) => e.preventDefault()}
                >
                  Forgot password?
                </a>
              </div>
              <InputWithIcon
                id="password"
                icon="lock"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                trailing={
                  <button
                    type="button"
                    className="text-outline transition-colors hover:text-primary"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <Icon name={showPassword ? "visibility_off" : "visibility"} size="md" />
                  </button>
                }
              />
            </div>

            <div className="pt-stack_md">
              <Button type="submit" size="lg" className="w-full text-white" loading={loading}>
                Sign In
                {!loading ? <Icon name="arrow_forward" size="md" className="text-white" /> : null}
              </Button>
            </div>
          </form>

          <div className="mt-section_gap border-t border-outline-variant pt-gutter">
            <div className="flex items-center justify-between text-on-surface-variant opacity-80">
              <div className="flex items-center gap-2">
                <Icon name="verified_user" size="sm" />
                <span className="text-label-md normal-case tracking-normal">
                  Secure SSL Encryption
                </span>
              </div>
              <span className="text-label-md normal-case tracking-normal">v0.1.0</span>
            </div>
          </div>
        </div>

      </main>

      {/* Background decoration */}
      <div className="pointer-events-none fixed right-0 top-0 -z-10 h-full w-1/3 select-none overflow-hidden opacity-30">
        <svg
          className="h-full w-full translate-x-1/2 fill-primary-container opacity-20"
          viewBox="0 0 100 100"
        >
          <path d="M0,0 L100,0 L100,100 Z" />
        </svg>
      </div>
    </div>
  );
}
